import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, AlertCircle, UserPlus, Mail, User, Shield, Users, Sparkles } from 'lucide-react';
import { adminAPI } from '../../../../services/adminAPI';
import Button from '../../../../components/common/Button';
import Alert from '../../../../components/common/Alert';

export default function AddUserModal({ isOpen, onClose, onSuccess }) {
  // State for form fields and error messages
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    is_domain_admin: false,
    assign_seat: false,
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const queryClient = useQueryClient();
  const emailInputRef = useRef(null);

  // Fetch the current admin data (includes email & organization_id)
  const {
    data: adminData,
    isLoading: isAdminLoading,
    error: adminError,
  } = useQuery({
    queryKey: ['currentAdmin'],
    queryFn: async () => {
      const response = await adminAPI.getCurrentAdmin();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: isOpen, // Only fetch when modal is open
  });

  // Focus the email field when the modal opens
  useEffect(() => {
    if (isOpen && emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, [isOpen]);

  // Domain validation function
  const validateEmailDomain = (email) => {
    if (!adminData?.email) return true;
    const adminDomain = adminData.email.split('@')[1];
    const userDomain = email.split('@')[1];
    return adminDomain === userDomain;
  };

  // Set up a mutation to create a new user
  const createUserMutation = useMutation({
    mutationFn: async (userData) => {
      // Include organization_id from adminData
      const payload = {
        ...userData,
        organization_id: adminData?.organization_id,
      };
      
      // Use the createUserWithSeat API if a seat should be assigned, otherwise use createUser
      if (userData.assign_seat) {
        return await adminAPI.createUserWithSeat(payload);
      } else {
        return await adminAPI.createUser(payload);
      }
    },
    onSuccess: () => {
      setSuccessMessage('User added successfully!');
      // Invalidate the adminUsers query so that the users list refreshes
      queryClient.invalidateQueries(['adminUsers']);
      queryClient.invalidateQueries(['subscription']);
      
      // Reset the form fields and clear errors
      setFormData({
        email: '',
        name: '',
        is_domain_admin: false,
        assign_seat: false,
      });
      
      // Call onSuccess if provided
      if (onSuccess) onSuccess();
      
      // Close the modal after a brief delay to show success message
      setTimeout(() => {
        onClose();
      }, 1500);
    },
    onError: (error) => {
      // Check for specific errors (e.g., user already exists)
      let msg =
        error.message ||
        error.response?.data?.message ||
        'Failed to add user';
      
      if (msg.toLowerCase().includes('already exists')) {
        msg = 'A user with this email already exists.';
      } else if (msg.toLowerCase().includes('no available seats')) {
        msg = 'No available seats. Please upgrade your subscription.';
      }
      
      setErrorMessage(msg);
    },
  });

  // If modal isn't open, render nothing
  if (!isOpen) return null;

  // Optionally show a loader if admin data is still loading
  if (isAdminLoading) {
    return (
      <div className="fixed inset-0 bg-midnight/70 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-violet"></div>
          </div>
        </div>
      </div>
    );
  }

  // If fetching admin data failed, show an error message
  if (adminError) {
    return (
      <div className="fixed inset-0 bg-midnight/70 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
          <Alert type="error">
            <AlertCircle className="w-4 h-4 mr-2" />
            Failed to load admin data. Please try again.
          </Alert>
          <div className="flex justify-end mt-4">
            <Button onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    );
  }

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.email || !formData.name) {
      setErrorMessage('Email and name are required.');
      return;
    }
    
    // Optional domain validation
    if (!validateEmailDomain(formData.email)) {
      setErrorMessage('Email domain does not match the organization domain.');
      return;
    }
    
    setErrorMessage('');
    setSuccessMessage('');
    createUserMutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 bg-midnight/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden transform transition-all">
        {/* Header */}
        <div className="bg-violet p-6 text-white">
          <div className="flex justify-between items-center">
            <h2 className="text-xl text-white font-bold flex items-center">
              <UserPlus className="w-6 text-white h-6 mr-2" />
              Add New User
            </h2>
            <button
              onClick={onClose}
              className="text-white/80 hover:text-white transition-colors hover:bg-white/20 rounded-full p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          {errorMessage && (
            <Alert type="error" className="mb-4">
              <AlertCircle className="w-4 h-4 mr-2" />
              {errorMessage}
            </Alert>
          )}
          
          {successMessage && (
            <Alert type="success" className="mb-4">
              <Sparkles className="w-4 h-4 mr-2" />
              {successMessage}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium text-midnight mb-1">
                Email Address
                {adminData?.email && (
                  <span className="text-xs text-ebony ml-2">
                    (e.g., ending with @{adminData.email.split('@')[1]})
                  </span>
                )}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Mail className="w-5 h-5 text-violet" />
                </div>
                <input
                  ref={emailInputRef}
                  type="email"
                  required
                  placeholder="user@example.com"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full pl-10 px-4 py-2.5 rounded-xl border-2 border-light-purple focus:border-violet focus:ring-2 focus:ring-violet/20 transition-all"
                />
              </div>
            </div>

            {/* Name Input */}
            <div>
              <label className="block text-sm font-medium text-midnight mb-1">
                Full Name
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <User className="w-5 h-5 text-violet" />
                </div>
                <input
                  type="text"
                  required
                  placeholder="John Smith"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  className="w-full pl-10 px-4 py-2.5 rounded-xl border-2 border-light-purple focus:border-violet focus:ring-2 focus:ring-violet/20 transition-all"
                />
              </div>
            </div>

            {/* Permission Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {/* Seat Allocation Card */}
              <div 
                className={`p-3 rounded-xl border-2 transition-colors ${formData.assign_seat ? 'border-green-500 bg-green-50' : 'border-gray-200'} cursor-pointer`}
                onClick={() => setFormData(prev => ({ ...prev, assign_seat: !prev.assign_seat }))}
              >
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="assignSeatCheckbox"
                    checked={formData.assign_seat}
                    onChange={(e) => setFormData({ ...formData, assign_seat: e.target.checked })}
                    className="rounded-full border-2 border-green-500 text-green-500 focus:ring-green-500 focus:ring-2"
                  />
                  <label htmlFor="assignSeatCheckbox" className="ml-2 flex items-center">
                    <Users className={`w-4 h-4 mr-1.5 ${formData.assign_seat ? 'text-green-500' : 'text-gray-500'}`} />
                    <span className={`text-sm font-medium ${formData.assign_seat ? 'text-green-700' : 'text-gray-700'}`}>
                      Seat Allocation
                    </span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  User can access the application
                </p>
              </div>
              
              {/* Admin Role Card */}
              <div 
                className={`p-3 rounded-xl border-2 transition-colors ${formData.is_domain_admin ? 'border-purple-500 bg-purple-50' : 'border-gray-200'} cursor-pointer`}
                onClick={() => setFormData(prev => ({ ...prev, is_domain_admin: !prev.is_domain_admin }))}
              >
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="domainAdminCheckbox"
                    checked={formData.is_domain_admin}
                    onChange={(e) => setFormData({ ...formData, is_domain_admin: e.target.checked })}
                    className="rounded-full border-2 border-purple-500 text-purple-500 focus:ring-purple-500 focus:ring-2"
                  />
                  <label htmlFor="domainAdminCheckbox" className="ml-2 flex items-center">
                    <Shield className={`w-4 h-4 mr-1.5 ${formData.is_domain_admin ? 'text-purple-500' : 'text-gray-500'}`} />
                    <span className={`text-sm font-medium ${formData.is_domain_admin ? 'text-purple-700' : 'text-gray-700'}`}>
                      Admin Access
                    </span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-6">
                  Can manage users and settings
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6">
              <Button 
                variant="outline" 
                onClick={onClose} 
                disabled={createUserMutation.isLoading}
                className="rounded-xl border-2 px-5"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createUserMutation.isLoading}
                className="rounded-xl px-5 bg-violet hover:bg-violet/90"
              >
                {createUserMutation.isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Adding...
                  </span>
                ) : (
                  'Add User'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}